/**
 * The operator console: what is running, what rooms are on it, who has an account here, and the
 * few things an operator is allowed to change about any of it.
 *
 * Everything here reads `trpc.admin`, which is gated on the server — this page is a convenience,
 * never the access control. The role check below only decides whether to draw a console or a
 * refusal; somebody with neither role who reaches the URL gets a panel rather than a page full
 * of failed queries.
 *
 * Two roles reach it, and the console is titled by whichever one the viewer holds. A moderator
 * sees the deployment and cannot touch it: the tabs they may not use are not drawn, and the
 * buttons that change something are not either. Both halves of that are re-decided by
 * `adminProcedure` on the server, which is the half that counts.
 *
 * The two queries poll on a few seconds, not on the tick: this is a view of a deployment, and a
 * faster refresh would buy an operator nothing but load on the hosts being watched. The
 * last-updated stamp and the refresh button exist so nobody has to guess how old a number is.
 */

import { BOT_TIER_ORDER, defaultRoomSettings } from '@chompdome/shared/constants';
import type { RoomSettings } from '@chompdome/shared/types';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import type { ProcessRow, RoomRow } from '../../../server/src/trpc/routers/admin.js';
import { type HostLimitsPatch, CapacityDialog } from '@/components/admin/capacity-dialog';
import { ModerationPanel } from '@/components/admin/moderation-panel';
import { RosterPanel } from '@/components/admin/roster-panel';
import { WorkersPanel } from '@/components/admin/workers-panel';
import { type RoomSettingsPatch, SettingsDialog } from '@/components/admin/settings-dialog';
import { UsersPanel } from '@/components/admin/users-panel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { useSession } from '@/hooks/use-session';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/admin')({
  component: AdminPage,
});

/** Match `USER_ROLES` in the server's `db/schema/users.ts`; the server is what enforces them. */
const ADMIN_ROLE = 'admin';
const MODERATOR_ROLE = 'moderator';

/** An operator view, not a game loop — see the capacity paragraph in CLAUDE.md. */
const POLL_INTERVAL_MS = 5_000;

/** Above this share of the tick budget a host is close enough to falling behind to shout about. */
const UTILIZATION_ALARM = 0.9;

function AdminPage() {
  const navigate = useNavigate();
  const { user, isLoading } = useSession();

  useEffect(() => {
    // Nobody at all is a sign-in problem, not an authorization one.
    if (!isLoading && !user) navigate({ to: '/login' });
  }, [isLoading, user, navigate]);

  if (isLoading || !user) {
    return <Notice title="Admin">Checking your session…</Notice>;
  }

  if (user.role !== ADMIN_ROLE && user.role !== MODERATOR_ROLE) {
    return (
      <Notice title="Not authorised">
        This console is for operators. Your account does not have the admin or moderator role.
      </Notice>
    );
  }

  return <AdminConsole isAdmin={user.role === ADMIN_ROLE} />;
}

function Notice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-1 justify-center p-4">
      <Card className="w-full max-w-md self-start">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{children}</p>
        </CardContent>
      </Card>
    </div>
  );
}

/** Which editor is open, and what it is pointed at. Only one is ever open at a time. */
type Editing =
  | { kind: 'room'; workerId: string; roomId: string; settings: RoomSettings }
  | { kind: 'defaults'; workerId: string; settings: RoomSettings };

/** The tabs, in the order they are drawn. `admin` is the lowest role each one is drawn for. */
const TABS = [
  { id: 'operations', label: 'Rooms & Processes', adminOnly: false },
  /**
   * One host at a time — its limits, the rooms assigned to it, and COMMITTED against MEASURED.
   * Admin-only: everything on it changes something, and a moderator's console is the two
   * read-only lists on the operations tab plus the moderation surface.
   */
  { id: 'workers', label: 'Workers', adminOnly: true },
  // Not admin-only, and the only writable tab that is not: banning is what the moderator role
  // exists for. `admin.moderation` is `moderatorProcedure` on the server, which is the half
  // that counts.
  { id: 'moderation', label: 'Moderation', adminOnly: false },
  { id: 'roster', label: 'Room roster', adminOnly: true },
  { id: 'users', label: 'Users', adminOnly: true },
] as const;

type TabId = (typeof TABS)[number]['id'];

function AdminConsole({ isAdmin }: { isAdmin: boolean }) {
  const utils = trpc.useUtils();
  const [tab, setTab] = useState<TabId>('operations');
  const processes = trpc.admin.listProcesses.useQuery(undefined, {
    refetchInterval: POLL_INTERVAL_MS,
  });
  const rooms = trpc.admin.listRooms.useQuery(undefined, { refetchInterval: POLL_INTERVAL_MS });

  const [editing, setEditing] = useState<Editing | null>(null);
  const [closing, setClosing] = useState<{ workerId: string; roomId: string } | null>(null);
  /** The host whose capacity knobs are open. Its own editor, beside the bot policy's. */
  const [capacity, setCapacity] = useState<ProcessRow | null>(null);

  const refreshAll = async () => {
    await Promise.all([utils.admin.listProcesses.invalidate(), utils.admin.listRooms.invalidate()]);
  };

  const updateRoomSettings = trpc.admin.updateRoomSettings.useMutation({
    onSuccess: async () => {
      setEditing(null);
      await refreshAll();
    },
  });
  const updateWorkerDefaults = trpc.admin.updateWorkerDefaults.useMutation({
    onSuccess: async () => {
      setEditing(null);
      await refreshAll();
    },
  });
  const closeRoom = trpc.admin.closeRoom.useMutation({
    onSuccess: async () => {
      setClosing(null);
      await refreshAll();
    },
  });
  const updateLimits = trpc.admin.updateLimits.useMutation({
    onSuccess: async () => {
      setCapacity(null);
      await refreshAll();
    },
  });
  const restoreDefaults = trpc.admin.restoreDefaults.useMutation({
    onSuccess: async () => {
      setCapacity(null);
      await refreshAll();
    },
  });

  const submitSettings = (patch: RoomSettingsPatch, confirmUnsafe: boolean) => {
    if (editing === null) return;
    if (editing.kind === 'room') {
      // No `confirmUnsafe`: a live room's only unmeasured knob is its world size, and that is
      // the one thing this dialog does not offer to change (`worldSizeEditable={false}`).
      updateRoomSettings.mutate({
        workerId: editing.workerId,
        roomId: editing.roomId,
        settings: patch,
      });
    } else {
      updateWorkerDefaults.mutate({
        workerId: editing.workerId,
        settings: patch,
        confirmUnsafe,
      });
    }
  };

  const lastUpdatedAt = Math.max(processes.dataUpdatedAt, rooms.dataUpdatedAt);
  const error = processes.error ?? rooms.error;
  const saving = editing?.kind === 'room' ? updateRoomSettings : updateWorkerDefaults;

  const tabs = TABS.filter((entry) => isAdmin || !entry.adminOnly);

  return (
    <div className="flex flex-1 justify-center p-4">
      {/* Wider than the app's ordinary content column, and still bounded. The Users roster is
          seven columns, two of which are now instants carrying a time, and every other tab is a
          table or a grid that simply gets more room; an unbounded full-bleed would give a
          27-inch monitor a line nobody can track back to its row. The tables keep their own
          `overflow-x-auto`, so a narrow window still scrolls the table rather than the page. */}
      <div className="w-full max-w-[100rem] space-y-4 self-start">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-black tracking-tight">{isAdmin ? 'Admin' : 'Moderator'}</h1>
          {tab === 'operations' && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span data-testid="last-updated">
                {lastUpdatedAt > 0 ? `Updated ${formatClock(lastUpdatedAt)}` : 'Never updated'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refreshAll()}
                disabled={processes.isFetching || rooms.isFetching}
              >
                {processes.isFetching || rooms.isFetching ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>
          )}
        </div>

        {/* Wraps rather than scrolls: two tabs fit on the narrowest phone, and a tab strip that
            scrolled sideways would hide the one an operator was looking for. */}
        <div role="tablist" aria-label="Console sections" className="flex flex-wrap gap-2">
          {tabs.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              onClick={() => setTab(entry.id)}
              className={`cursor-pointer rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                tab === entry.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {tab === 'users' ? (
          <div role="tabpanel" aria-label="Users">
            {/* The tab is admin-only already, so this is the belt to that brace: the delete
                control is not drawn under any code path that could reach this component with a
                moderator behind it, and `admin.users.remove` is `adminProcedure` regardless. */}
            <UsersPanel canDelete={isAdmin} />
          </div>
        ) : tab === 'moderation' ? (
          <div role="tabpanel" aria-label="Moderation">
            <ModerationPanel />
          </div>
        ) : tab === 'workers' ? (
          <div role="tabpanel" aria-label="Workers">
            <WorkersPanel
              processes={processes.data ?? []}
              rooms={rooms.data ?? []}
              isLoading={processes.isLoading}
              onEditCapacity={setCapacity}
            />
          </div>
        ) : tab === 'roster' ? (
          <div role="tabpanel" aria-label="Room roster">
            <RosterPanel
              hostIds={(processes.data ?? []).map((row) => row.id)}
              // A declaration made while a host is unreachable still needs numbers to open on;
              // the server replaces them with that host's own defaults when it can answer.
              defaultSettings={
                (processes.data ?? []).find((row) => row.defaults !== null)?.defaults ??
                defaultRoomSettings()
              }
            />
          </div>
        ) : (
          <div role="tabpanel" aria-label="Rooms & Processes" className="space-y-4">
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error.message}
              </p>
            )}

            <ProcessesSection
              rows={processes.data ?? []}
              isLoading={processes.isLoading}
              canManage={isAdmin}
              onEditDefaults={(row) => {
                if (row.defaults === null) return;
                setEditing({ kind: 'defaults', workerId: row.id, settings: row.defaults });
              }}
              onEditCapacity={setCapacity}
            />

            <RoomsSection
              rows={rooms.data ?? []}
              isLoading={rooms.isLoading}
              canManage={isAdmin}
              onEdit={(row) =>
                setEditing({
                  kind: 'room',
                  workerId: row.workerId,
                  roomId: row.room.id,
                  settings: row.room.settings,
                })
              }
              onClose={(row) => setClosing({ workerId: row.workerId, roomId: row.room.id })}
            />
          </div>
        )}

        {editing && (
          <SettingsDialog
            open
            title={
              editing.kind === 'room'
                ? `Room ${editing.roomId}`
                : `Default settings on ${editing.workerId}`
            }
            description={
              editing.kind === 'room'
                ? 'Applies to this room only, immediately — the bot mix and the access rules are read where a player is placed, so they take effect on the next join. Its world size and its mode are FIXED and are drawn here as read-only: the room was built inside those walls and the round it is playing was decided when it opened. To change either, edit the declaration on Room roster and then Close this room, which restarts it under the same id.'
                : 'Applies to rooms opened on this host from now on. Rooms already running keep the settings they opened with.'
            }
            settings={editing.settings}
            serverError={saving.error?.message ?? null}
            pending={saving.isPending}
            // A running room was built inside its walls; only the size its host's *next* rooms
            // open at is editable here. See `liveRoomSettingsPatchSchema`.
            worldSizeEditable={editing.kind === 'defaults'}
            // And its mode is the same case: the round it is playing and the way it enrolled the
            // people in it were decided when it opened, so the selector is drawn only where it
            // can be honoured — a host's defaults decide what its *next* rooms open as.
            modeEditable={editing.kind === 'defaults'}
            onSubmit={submitSettings}
            onClose={() => setEditing(null)}
          />
        )}

        {capacity?.limits != null && (
          <CapacityDialog
            open
            workerId={capacity.id}
            hostKind={capacity.hostKind}
            limits={capacity.limits}
            measured={capacity.measuredLimits}
            safe={capacity.safeLimits}
            // The whole fleet, so the dialog can run the same lock guard the server runs: with
            // no unlocked host left, a player the roster cannot seat is refused everywhere.
            fleet={(processes.data ?? []).map((row) => ({ id: row.id, locked: row.locked }))}
            serverError={updateLimits.error?.message ?? restoreDefaults.error?.message ?? null}
            pending={updateLimits.isPending}
            restorePending={restoreDefaults.isPending}
            onSubmit={(limits: HostLimitsPatch, confirmUnsafe: boolean) =>
              updateLimits.mutate({ workerId: capacity.id, limits, confirmUnsafe })
            }
            onRestore={() => restoreDefaults.mutate({ workerId: capacity.id })}
            onClose={() => setCapacity(null)}
          />
        )}

        {closing && (
          <Dialog
            open
            title="Close this room?"
            description={`Closing ${closing.roomId} ends the matches being played in it. Everyone still inside is recorded as a finished match, told the room is going away, and has to re-join — they will be placed elsewhere. This is also how a DECLARED room is restarted: it comes back under the same id, with whatever is on its declaration, the next time somebody is placed into it — which is the only way to change the mode or the world size of a room that is already running.`}
            onClose={() => setClosing(null)}
          >
            {closeRoom.error && (
              <p className="mb-3 text-sm text-destructive" role="alert">
                {closeRoom.error.message}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setClosing(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={closeRoom.isPending}
                onClick={() => closeRoom.mutate(closing)}
              >
                {closeRoom.isPending ? 'Closing…' : 'Close room'}
              </Button>
            </div>
          </Dialog>
        )}
      </div>
    </div>
  );
}

function ProcessesSection({
  rows,
  isLoading,
  canManage,
  onEditDefaults,
  onEditCapacity,
}: {
  rows: readonly ProcessRow[];
  isLoading: boolean;
  /** False for a moderator: the figures are theirs to read, the knobs are not theirs to turn. */
  canManage: boolean;
  onEditDefaults: (row: ProcessRow) => void;
  onEditCapacity: (row: ProcessRow) => void;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Processes
      </h2>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No room hosts are registered.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((row) => (
            <ProcessCard
              key={row.id}
              row={row}
              canManage={canManage}
              onEditDefaults={onEditDefaults}
              onEditCapacity={onEditCapacity}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ProcessCard({
  row,
  canManage,
  onEditDefaults,
  onEditCapacity,
}: {
  row: ProcessRow;
  canManage: boolean;
  onEditDefaults: (row: ProcessRow) => void;
  onEditCapacity: (row: ProcessRow) => void;
}) {
  return (
    <Card className={row.reachable ? undefined : 'opacity-60'}>
      <CardHeader className="gap-2 pb-4">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="break-all text-base">{row.id}</CardTitle>
          <Badge tone="neutral">{row.embedded ? 'embedded' : 'remote'}</Badge>
        </div>
        <CardDescription className="break-all">{row.wsUrl}</CardDescription>
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={row.reachable ? 'good' : 'bad'}>
            {row.reachable ? 'reachable' : 'unreachable'}
          </Badge>
          {row.draining && <Badge tone="warn">draining</Badge>}
          {row.reachable && !row.draining && (
            <Badge tone={row.accepting ? 'good' : 'warn'}>
              {row.accepting ? 'accepting' : 'not accepting'}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {row.reachable ? (
          <>
            <Utilization value={row.utilization} />
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <Figure label="Rooms" value={formatCount(row.roomCount)} />
              <Figure label="Humans" value={formatCount(row.humanCount)} />
              <Figure label="Bots" value={formatCount(row.botCount)} />
              <Figure
                label="Uptime"
                value={row.uptimeMs === null ? '—' : formatAge(row.uptimeMs)}
              />
              <Figure label="Version" value={row.version ?? '—'} />
              <Figure label="Worker id" value={row.workerId ?? '—'} />
              <Figure
                label="Room cap"
                value={row.limits === null ? '—' : row.limits.maxRooms.toLocaleString()}
              />
              <Figure
                label="Seats per room"
                value={row.limits === null ? '—' : row.limits.maxPlayers.toLocaleString()}
              />
              {/* The measured knob, and the only one of the three that is a capacity plan: the
                  two above are backstops whose product is deliberately far past it. */}
              <Figure
                label="Player ceiling"
                value={row.limits === null ? '—' : row.limits.maxConcurrentPlayers.toLocaleString()}
              />
            </dl>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            This host did not answer its probe. Its rooms are not listed below.
          </p>
        )}

        {canManage && (
          <div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={row.defaults === null}
                onClick={() => onEditDefaults(row)}
              >
                Edit defaults
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={row.limits === null}
                onClick={() => onEditCapacity(row)}
              >
                Capacity
              </Button>
            </div>
            {(row.defaults === null || row.limits === null) && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                Defaults are unavailable while the host is unreachable.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Utilization({ value }: { value: number | null }) {
  if (value === null) {
    return <p className="text-sm text-muted-foreground">Tick utilization unknown</p>;
  }

  const percent = value * 100;
  const alarming = value >= UTILIZATION_ALARM;

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-muted-foreground">Tick utilization</span>
        <span
          data-testid="utilization"
          className={`font-semibold tabular-nums ${alarming ? 'text-destructive' : ''}`}
        >
          {percent.toFixed(1)}%
        </span>
      </div>
      {/* Decorative: the figure above it is the accessible copy of the same number. */}
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <div
          className={`h-full rounded-full ${alarming ? 'bg-destructive' : 'bg-primary'}`}
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </div>
    </div>
  );
}

function RoomsSection({
  rows,
  isLoading,
  canManage,
  onEdit,
  onClose,
}: {
  rows: readonly RoomRow[];
  isLoading: boolean;
  /** False for a moderator, who gets the table and neither of its two buttons. */
  canManage: boolean;
  onEdit: (row: RoomRow) => void;
  onClose: (row: RoomRow) => void;
}) {
  const now = Date.now();

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Rooms</h2>
      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No rooms are open on any reachable host.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Room</th>
                    <th className="py-2 pr-3 font-medium">Host</th>
                    <th className="py-2 pr-3 text-right font-medium">Age</th>
                    <th className="py-2 pr-3 text-right font-medium">Tick</th>
                    <th className="py-2 pr-3 text-right font-medium">Humans / bots / max</th>
                    <th className="py-2 pr-3 text-right font-medium">Mean step</th>
                    <th className="py-2 pr-3 font-medium">Settings</th>
                    {canManage && (
                      <th className="py-2 font-medium">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={`${row.workerId}:${row.room.id}`}
                      className="border-b border-border/60 last:border-0"
                    >
                      <td className="py-2 pr-3 font-medium">
                        {/* EVERY room has a name now — the declared one, or the seeded one the
                            host gave a room the matchmaker invented — so the name always leads.
                            The id stays under it because this is the console: it is the address
                            every action here is issued against and what a share link carries. */}
                        <span>{row.room.name}</span>
                        <span className="block break-all text-xs text-muted-foreground">
                          {row.room.id}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">{row.workerId}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                        {formatAge(Math.max(0, now - row.room.createdAtMs))}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                        {row.room.tick.toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {row.room.humanCount} / {row.room.botCount} / {row.room.maxPlayers}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                        {row.room.meanStepMs.toFixed(2)} ms
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {settingsSummary(row.room.settings)}
                      </td>
                      {canManage && (
                        <td className="py-2">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onEdit(row)}
                              aria-label={`Edit settings for ${row.room.id}`}
                            >
                              Settings
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => onClose(row)}
                              aria-label={`Close room ${row.room.id}`}
                            >
                              Close
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-all font-medium tabular-nums">{value}</dd>
    </div>
  );
}

const BADGE_TONES = {
  good: 'bg-primary/15 text-primary',
  warn: 'bg-accent/15 text-accent',
  bad: 'bg-destructive/15 text-destructive',
  neutral: 'bg-muted text-muted-foreground',
} as const;

function Badge({ tone, children }: { tone: keyof typeof BADGE_TONES; children: ReactNode }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold ${BADGE_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

function settingsSummary(settings: RoomSettings): string {
  if (!settings.botsEnabled) return 'bots off';
  const mix = BOT_TIER_ORDER.map((tier) => settings.tierWeights[tier]).join('/');
  return `bots on · fill to ${settings.minPlayers} · ≤ ${settings.maxBots} bots · ${mix}`;
}

function formatCount(value: number | null): string {
  return value === null ? '—' : value.toLocaleString();
}

function formatAge(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

function formatClock(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString();
}
